import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { withAppHOC } from '../../hoc';
import {
  StyledContentContainer,
  StyledMainAreaContainer,
} from '../../components/styled';
import PageHeader from '../../components/PageHeader/PageHeader';
import {
  Form,
  Fieldset,
  Label,
  TextInput,
  Dropdown,
  MessageBox,
  Button,
} from '../../components/FormElements';
import API from '../../api';
import { RESPONSE_STATUSES } from '../../constants';
import { getReturnUrl } from '../../utils';
import { Image } from '../../components/Image';

const StyledBankAccounts = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const StyledBankAccountsOuterCard = styled.section`
  border: 2px solid #ffffff;
  padding: 0px;
  border-radius: 12px;
  cursor: pointer;
  transition: border 0.3s, box-shadow 0.3s;

  &.active {
    border-color: #ce8b54;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:hover {
    border-color: #ce8b54;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const StyledBankAccountsCard = styled.section`
  display: flex;
  align-items: center;
  padding: 15px;
  gap: 15px;

  h1 {
    font-size: 18px;
    margin: 0 0 5px 0;
  }

  p {
    font-size: 14px;
    margin: 2px 0;
    word-break: break-word;
  }

  @media (max-width: 500px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StyledBankLogoContainer = styled.section`
  width: 50px;
  height: 50px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const StyledBankDetailsContainer = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const WalletTopupsCreate = () => {
  const [state, setState] = useState({
    isLoading: false,
    responseStatus: '',
    responseMessage: '',
    banksId: '',
    paymentType: '',
    walletType: 'GST',
    amount: '',
    utrNumber: '',
  });

  const readMeta = async () => {
    try {
      const response = await API.get(`/meta`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data: meta } = useQuery({
    queryKey: ['meta'],
    queryFn: readMeta,
  });

  const paymentTypeOptions = [
    { name: '-- SELECT PAYMENT TYPE --', value: '' },
    { name: 'IMPS', value: 'IMPS' },
    { name: 'NEFT', value: 'NEFT' },
    { name: 'RTGS', value: 'RTGS' },
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: '',
      responseMessage: '',
    }));
    try {
      const response = await API.post('/payments/wallet-topups', {
        banksId: state.banksId,
        paymentType: state.paymentType,
        walletType: state.walletType,
        amount: state.amount,
        utrNumber: state.utrNumber,
      });
      const { status, message } = response.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.SUCCESS,
          responseMessage: message,
          banksId: '',
          paymentType: '',
          walletType: 'GST',
          amount: '',
          utrNumber: '',
        }));
        setTimeout(() => {
          setState((prevState) => ({
            ...prevState,
            responseStatus: '',
            responseMessage: '',
          }));
          window.location.href = getReturnUrl('create');
        }, 3000);
      } else {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.FAILED,
          responseMessage: message,
        }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  const renderPageHeading = () => (
    <PageHeader title="Topup Wallet" showBackButton={true} />
  );

  const adminBankAccounts = meta?.adminBankAccounts || [];

  return (
    <>
      {renderPageHeading()}
      <StyledContentContainer>
        <StyledMainAreaContainer>
          <Form
            action="#"
            method="POST"
            onSubmit={onSubmit}
            style={{ maxWidth: '500px' }}
          >
            <Fieldset>
              <Label required>Select Bank Account</Label>
            </Fieldset>
            <StyledBankAccounts>
              {adminBankAccounts.map(
                ({
                  id,
                  bankName,
                  bankLogo,
                  payeeName,
                  accountNumber,
                  ifscCode,
                }) => (
                  <StyledBankAccountsOuterCard
                    key={id}
                    className={id === state.banksId ? 'active' : ''}
                  >
                    <StyledBankAccountsCard
                      onClick={() =>
                        setState((prevState) => ({
                          ...prevState,
                          banksId: id,
                        }))
                      }
                    >
                      <StyledBankLogoContainer>
                        <Image source={bankLogo || 'no-image.png'} alt={bankName} />
                      </StyledBankLogoContainer>
                      <StyledBankDetailsContainer>
                        <h1>{bankName}</h1>
                        <p>{payeeName}</p>
                        <p>{accountNumber}</p>
                        <p>{ifscCode}</p>
                      </StyledBankDetailsContainer>
                    </StyledBankAccountsCard>
                  </StyledBankAccountsOuterCard>
                )
              )}
            </StyledBankAccounts>
            <Fieldset>
              <Label required>Payment Type</Label>
              <Dropdown
                value={state.paymentType}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    paymentType: value,
                  }))
                }
                placeholder="Payment Type"
                disabled={state.isLoading}
                options={paymentTypeOptions}
              />
            </Fieldset>
            <Fieldset>
              <Label required>Amount</Label>
              <TextInput
                value={state.amount}
                isNumeric={true}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    amount: value,
                  }))
                }
                placeholder="Amount"
                disabled={state.isLoading}
              />
            </Fieldset>
            <Fieldset>
              <Label required>UTR Number</Label>
              <TextInput
                value={state.utrNumber}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    utrNumber: value,
                  }))
                }
                placeholder="UTR Number"
                disabled={state.isLoading}
              />
            </Fieldset>
            {state.responseStatus && state.responseMessage && (
              <Fieldset>
                <MessageBox
                  status={state.responseStatus}
                  message={state.responseMessage}
                />
              </Fieldset>
            )}
            <Fieldset>
              <Button disabled={state.isLoading}>Create</Button>
            </Fieldset>
          </Form>
        </StyledMainAreaContainer>
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(WalletTopupsCreate);
